import { useEffect, useState } from 'react';
import { ops as opsApi, sysupdates as sysApi } from '../lib/api';
import {
  Activity, AlertTriangle, BellRing, Bell, ShieldAlert, ShieldCheck, ClipboardCheck,
  FileSearch, Truck, Users, Database, ScrollText, RefreshCw, Clock, ChevronRight, Loader2,
} from 'lucide-react';

/**
 * Admin Operations Console.
 * Aggregates 10 attention-worthy signals from across the platform into a single page.
 */
export default function AdminOpsConsole() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    try {
      const d = await opsApi.dashboard();
      setData(d);
    } catch (e) {
      console.error('ops dashboard load failed', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 60_000); // poll every 60s
    return () => clearInterval(id);
  }, []);

  if (loading) return <div className="py-10 text-center text-ink/60 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading operations console…</div>;
  if (!data) return <div className="py-10 text-center text-ink/60">Could not load dashboard.</div>;

  const c = data.cards;
  const healthStyle = {
    healthy:   { ring: 'ring-[#A5D6A7]', bg: 'bg-[#E8F5E9]', text: 'text-[#1B5E20]', label: 'Healthy', icon: ShieldCheck },
    attention: { ring: 'ring-[#FFD180]', bg: 'bg-[#FFF8E1]', text: 'text-[#8E4A00]', label: 'Needs Attention', icon: BellRing },
    critical:  { ring: 'ring-[#EF9A9A]', bg: 'bg-[#FFEBEE]', text: 'text-[#B71C1C]', label: 'Critical', icon: ShieldAlert },
  }[data.health] || {};
  const HealthIcon = healthStyle.icon || Activity;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4 flex-wrap">
        <div className={`flex items-center gap-2 px-4 py-3 rounded-xl ring-2 ${healthStyle.ring} ${healthStyle.bg} ${healthStyle.text}`} data-testid="ops-health-badge">
          <HealthIcon className="w-6 h-6"/>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold">Operations</div>
            <div className="font-heading font-black text-xl leading-none">{healthStyle.label}</div>
          </div>
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-ink/60">Total items needing attention</div>
          <div className="font-heading font-black text-3xl" data-testid="ops-total-attention">{data.total_attention}</div>
          <div className="text-[11px] text-ink/50 mt-1 inline-flex items-center gap-1"><Clock className="w-3 h-3"/>{new Date(data.generated_at).toLocaleString()}</div>
        </div>
        <button
          data-testid="ops-refresh-btn"
          onClick={() => { setRefreshing(true); fetchData(); }}
          className="rounded-full border border-border bg-white px-4 py-2 text-sm font-semibold hover:bg-muted inline-flex items-center gap-1.5"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`}/>
          Refresh
        </button>
      </div>

      {/* Top critical row */}
      <div className="grid md:grid-cols-3 gap-4">
        <OpsCard
          tone="critical"
          Icon={ShieldAlert}
          title="SOS — Panic alerts open"
          count={c.sos.count}
          testid="ops-card-sos"
          empty="No open SOS"
        >
          {c.sos.items.map((s) => (
            <Row key={s.id} testid={`ops-sos-${s.id}`}>
              <Pill tone="red">{s.sos_code}</Pill>
              <Lead>{s.user_name} <span className="text-ink/50">· {s.user_role}</span></Lead>
              <Sub>{s.note || '—'}</Sub>
              <Time at={s.created_at}/>
            </Row>
          ))}
        </OpsCard>

        <OpsCard tone="critical" Icon={Database} title="Open data breaches" count={c.open_breaches.count} testid="ops-card-breaches" empty="No open breaches">
          {c.open_breaches.items.map((b) => (
            <Row key={b.id} testid={`ops-breach-${b.id}`}>
              <Pill tone={b.severity === 'critical' || b.severity === 'high' ? 'red' : 'amber'}>{b.breach_code}</Pill>
              <Lead>{b.summary?.slice(0, 80) || '—'}</Lead>
              <Sub>severity: <b>{b.severity}</b> · {b.category}</Sub>
              <Sub className="text-[#B71C1C]">DPDP deadline: {new Date(b.dpdp_notification_due_by).toLocaleString()}</Sub>
            </Row>
          ))}
        </OpsCard>

        <OpsCard tone="critical" Icon={Truck} title="Shipment SLA breaches" count={c.shipment_sla_breaches.count} testid="ops-card-ship-sla" empty="All shipments on time">
          {c.shipment_sla_breaches.items.map((s) => (
            <Row key={s.id} testid={`ops-ship-${s.id}`}>
              <Pill tone="red">{s.shipment_code}</Pill>
              <Lead>{s.village} · {s.agent_name || 'unassigned'}</Lead>
              <Sub>status: <b>{s.status}</b></Sub>
              {s.sla?.deliver_by && <Sub>deliver by {new Date(s.sla.deliver_by).toLocaleString()}</Sub>}
            </Row>
          ))}
        </OpsCard>
      </div>

      {/* Secondary attention row */}
      <div className="grid md:grid-cols-3 gap-4">
        <OpsCard tone="amber" Icon={AlertTriangle} title="Tickets — SLA breached" count={c.breached_tickets.count} testid="ops-card-tickets" empty="No breached tickets">
          {c.breached_tickets.items.map((t) => (
            <Row key={t.id} testid={`ops-tkt-${t.id}`}>
              <Pill tone="amber">{t.ticket_code}</Pill>
              <Lead>{t.subject}</Lead>
              <Sub>{t.requester_name} · {t.priority} · {t.status}</Sub>
              <Sub>assignee: <b>{t.assignee_emp_code || 'unassigned'}</b></Sub>
            </Row>
          ))}
        </OpsCard>

        <OpsCard tone="amber" Icon={FileSearch} title="Grievances — overdue (>30d)" count={c.overdue_grievances.count} testid="ops-card-grievances" empty="All grievances within SLA">
          {c.overdue_grievances.items.map((g) => (
            <Row key={g.id} testid={`ops-grv-${g.id}`}>
              <Pill tone="amber">{g.grievance_code}</Pill>
              <Lead>{g.subject}</Lead>
              <Sub>{g.category} · {g.user_phone}</Sub>
              <Sub className="text-[#B71C1C]">due: {new Date(g.sla_due_at).toLocaleString()}</Sub>
            </Row>
          ))}
        </OpsCard>

        <OpsCard tone="amber" Icon={ClipboardCheck} title="Compliance reviews — pending" count={c.compliance_reviews.count} testid="ops-card-compliance" empty="No pending product reviews">
          {c.compliance_reviews.items.map((p) => (
            <Row key={p.id} testid={`ops-cmp-${p.id}`}>
              <Pill tone="amber">PROD</Pill>
              <Lead>{p.name}</Lead>
              <Sub>price ₹{Number(p.price || 0).toFixed(2)}</Sub>
              <Sub>review needed</Sub>
            </Row>
          ))}
        </OpsCard>
      </div>

      {/* Informational row */}
      <div className="grid md:grid-cols-3 gap-4">
        <OpsCard tone="info" Icon={Users} title="QMS — suspended / blacklisted" count={c.qms_suspended.count} testid="ops-card-qms" empty="No entities under action">
          {c.qms_suspended.items.map((q) => (
            <Row key={q.id} testid={`ops-qms-${q.id}`}>
              <Pill tone="red">{q.tier_effective}</Pill>
              <Lead>{q.entity_name || q.entity_id}</Lead>
              <Sub>{q.entity_type} · score {q.score}</Sub>
            </Row>
          ))}
        </OpsCard>

        <OpsCard tone="info" Icon={ScrollText} title="Business audits in flight" count={c.in_flight_audits.count} testid="ops-card-audits" empty="No active audits">
          {c.in_flight_audits.items.map((a) => (
            <Row key={a.id} testid={`ops-aud-${a.id}`}>
              <Pill tone="blue">{a.audit_code}</Pill>
              <Lead>{a.objective}</Lead>
              <Sub>{a.scope} · {a.subject_kind} · {a.status}</Sub>
            </Row>
          ))}
        </OpsCard>

        <OpsCard tone="info" Icon={Database} title="DSAR — deletion requests pending" count={c.dsar_pending_deletion.count} testid="ops-card-dsar" empty="No pending DSAR">
          {c.dsar_pending_deletion.items.map((d) => (
            <Row key={d.id} testid={`ops-dsar-${d.id}`}>
              <Pill tone="amber">DSAR</Pill>
              <Lead>{d.user_phone}</Lead>
              <Sub>{d.reason || '—'}</Sub>
              <Sub>effective: {d.effective_delete_at ? new Date(d.effective_delete_at).toLocaleDateString() : '—'}</Sub>
            </Row>
          ))}
        </OpsCard>
      </div>

      {/* System updates feed */}
      <SysUpdatesPanel items={c.sysupdates.items} totalUnread={c.sysupdates.count} onRead={fetchData}/>
    </div>
  );
}

// ----- subcomponents -----
function OpsCard({ tone = 'info', Icon, title, count, children, testid, empty }) {
  const tones = {
    critical: { ring: 'border-[#EF9A9A]', badge: 'bg-[#B71C1C] text-white' },
    amber:    { ring: 'border-[#FFD180]', badge: 'bg-[#E67E22] text-white' },
    info:     { ring: 'border-border',    badge: 'bg-[#0D47A1] text-white' },
  }[tone] || {};
  const hasItems = Array.isArray(children) ? children.length > 0 : !!children;
  return (
    <section data-testid={testid} className={`rounded-xl bg-white border ${tones.ring} p-4 shadow-sm`}>
      <div className="flex items-start gap-2">
        <div className={`w-9 h-9 rounded-lg ${tones.badge} grid place-items-center shrink-0`}>
          <Icon className="w-4 h-4"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60">Card</div>
          <div className="font-heading font-bold text-base leading-tight">{title}</div>
        </div>
        <div className="font-heading font-black text-2xl tabular-nums" data-testid={`${testid}-count`}>{count}</div>
      </div>
      <div className="mt-3 space-y-2">
        {hasItems ? children : (
          <div className="text-xs text-ink/40 py-3 text-center" data-testid={`${testid}-empty`}>{empty}</div>
        )}
      </div>
    </section>
  );
}

function Row({ children, testid }) {
  return (
    <div data-testid={testid} className="rounded-md bg-[#FBF8F1] border border-border px-3 py-2 text-[12px]">
      {children}
    </div>
  );
}

function Lead({ children }) {
  return <div className="font-semibold text-ink truncate">{children}</div>;
}

function Sub({ children, className = '' }) {
  return <div className={`text-[11px] text-ink/60 ${className}`}>{children}</div>;
}

function Time({ at }) {
  return <Sub>{new Date(at).toLocaleString()}</Sub>;
}

function Pill({ children, tone = 'blue' }) {
  const tones = {
    red:   'bg-[#FFEBEE] text-[#B71C1C] border-[#EF9A9A]',
    amber: 'bg-[#FFF8E1] text-[#8E4A00] border-[#FFD180]',
    blue:  'bg-[#E3F2FD] text-[#0D47A1] border-[#90CAF9]',
    green: 'bg-[#E8F5E9] text-[#1B5E20] border-[#A5D6A7]',
  }[tone] || tones.blue;
  return (
    <span className={`inline-block text-[10px] font-bold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded border ${tones} mr-2`}>
      {children}
    </span>
  );
}

function SysUpdatesPanel({ items, totalUnread, onRead }) {
  const markRead = async (id) => {
    try { await sysApi.markRead(id); onRead?.(); } catch (e) { /* swallow */ }
  };
  return (
    <section data-testid="ops-sysupdates-panel" className="rounded-xl bg-white border border-border p-4 shadow-sm">
      <div className="flex items-start gap-2">
        <div className="w-9 h-9 rounded-lg bg-[#0D47A1] text-white grid place-items-center"><Bell className="w-4 h-4"/></div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60">System updates</div>
          <div className="font-heading font-bold text-base">What changed on the platform</div>
        </div>
        <div className="font-heading font-black text-2xl tabular-nums" data-testid="ops-sysupdates-count">{totalUnread}</div>
      </div>
      <div className="mt-3 space-y-2">
        {items.length === 0 ? (
          <div className="text-xs text-ink/40 py-3 text-center">You're caught up on all updates.</div>
        ) : items.map((u) => (
          <div key={u.id} data-testid={`ops-sysupdate-${u.id}`} className="rounded-md bg-[#FBF8F1] border border-border px-3 py-2 flex items-start gap-2">
            <Pill tone={u.severity === 'high' || u.severity === 'critical' ? 'red' : (u.severity === 'medium' ? 'amber' : 'blue')}>{u.kind}</Pill>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] text-ink/50 tracking-wider">{u.version}</div>
              <div className="font-semibold text-ink text-[13px]">{u.title}</div>
            </div>
            <button
              data-testid={`ops-sysupdate-read-${u.id}`}
              onClick={() => markRead(u.id)}
              className="text-[11px] font-semibold text-[#0D47A1] hover:underline inline-flex items-center gap-1"
            >
              Mark read <ChevronRight className="w-3 h-3"/>
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
